"use client";

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

const IDF_PREFIXES = ["75", "77", "78", "91", "92", "93", "94", "95", "60"];

export function isIleDeFrance(postalCode: string): boolean {
  if (!postalCode || postalCode.length < 2) return false;
  const prefix = postalCode.substring(0, 2);
  return IDF_PREFIXES.includes(prefix);
}

/** Valide un CP : 2 chiffres (département) ou 5 chiffres (code postal complet) */
function validatePostalCode(cp: string): { valid: boolean; cleaned: string; error: string | null } {
  const cleaned = cp.trim().replace(/\D/g, "");
  if (cleaned.length === 0) {
    return { valid: false, cleaned, error: "Veuillez entrer un code postal" };
  }
  if (cleaned.length === 1 || cleaned.length === 3 || cleaned.length === 4) {
    return { valid: false, cleaned, error: "Entrez 2 chiffres (département) ou 5 chiffres (code postal)" };
  }
  if (cleaned.length > 5) {
    return { valid: false, cleaned, error: "Code postal trop long (5 chiffres max)" };
  }
  // 2 digits: check it's a valid department (01-95 + 2A/2B handled as 20, DOM 97x)
  if (cleaned.length === 2) {
    const num = parseInt(cleaned);
    if (num < 1 || (num > 95 && num !== 97)) {
      return { valid: false, cleaned, error: "Département invalide" };
    }
    return { valid: true, cleaned, error: null };
  }
  // 5 digits
  const prefix = parseInt(cleaned.substring(0, 2));
  if (prefix < 1 || (prefix > 95 && prefix < 97) || prefix > 98) {
    return { valid: false, cleaned, error: "Code postal invalide" };
  }
  return { valid: true, cleaned, error: null };
}

interface PostalCodeContextType {
  postalCode: string | null;
  /** Retourne null si OK, ou un message d'erreur */
  setPostalCode: (cp: string) => string | null;
  clearPostalCode: () => void;
  isIDF: boolean;
  hasPostalCode: boolean;
}

const PostalCodeContext = createContext<PostalCodeContextType>({
  postalCode: null,
  setPostalCode: () => null,
  clearPostalCode: () => {},
  isIDF: false,
  hasPostalCode: false,
});

const STORAGE_KEY = "toolouer_postal_code";

export function PostalCodeProvider({ children }: { children: ReactNode }) {
  const [postalCode, setPostalCodeState] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setPostalCodeState(stored);
    }
    setMounted(true);
  }, []);

  const setPostalCode = useCallback((cp: string): string | null => {
    const { valid, cleaned, error } = validatePostalCode(cp);
    if (!valid) return error;
    localStorage.setItem(STORAGE_KEY, cleaned);
    setPostalCodeState(cleaned);
    return null;
  }, []);

  const clearPostalCode = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setPostalCodeState(null);
  }, []);

  const isIDF = postalCode ? isIleDeFrance(postalCode) : false;
  const hasPostalCode = !!postalCode;

  if (!mounted) {
    return <>{children}</>;
  }

  return (
    <PostalCodeContext.Provider value={{ postalCode, setPostalCode, clearPostalCode, isIDF, hasPostalCode }}>
      {children}
    </PostalCodeContext.Provider>
  );
}

export function usePostalCode() {
  return useContext(PostalCodeContext);
}
